import * as React from 'react';
import { observer } from 'mobx-react';

import { ComponentType } from 'shared/entities/components/Component';
import { ButtonColor } from 'shared/newEntities/components/Button';
import FormModal from 'shared/newComponents/modals/FormModal';
import { ModalSize } from 'shared/newEntities/components/modals';
import { ChannelsModalStore } from 'stores/ChannelsModalStore';
import { ChannelScenariosStore } from 'stores/ChannelsModalStore/ChannelScenariosStore';

type DescriptionParams = {
  editedChannelName: string | null;
  pendingChannelChangeIsSelection: boolean;
};

type Props = {
  channelsModalStore: ChannelsModalStore;
  channelScenariosStore: ChannelScenariosStore;
  title: string;
  discardText: string;
  saveText: string;
  getDescription: (params: DescriptionParams) => React.ReactNode;
  onPendingCloseApplied?: () => void;
};

const ChannelScenariosNavigationConfirmModal: React.FC<Props> = ({
  channelsModalStore,
  channelScenariosStore,
  title,
  discardText,
  saveText,
  getDescription,
  onPendingCloseApplied
}) => {
  const handleDiscard = React.useCallback(() => {
    const pendingChannelAction =
      channelsModalStore.discardPendingChannelChange();

    if (pendingChannelAction?.type === 'close') {
      onPendingCloseApplied?.();
    }
  }, [channelsModalStore, onPendingCloseApplied]);

  const handleSave = React.useCallback(async () => {
    const pendingChannelAction =
      await channelsModalStore.saveAndApplyPendingChannelChange();

    if (pendingChannelAction?.type === 'close') {
      onPendingCloseApplied?.();
    }
  }, [channelsModalStore, onPendingCloseApplied]);

  return (
    <FormModal
      title={title}
      opened={channelsModalStore.pendingNavigationConfirmationOpened}
      onClose={channelsModalStore.cancelPendingChannelChange}
      size={ModalSize.xs}
      cancel={{
        type: ComponentType.button,
        onClick: handleDiscard,
        children: discardText
      }}
      ok={{
        type: ComponentType.button,
        onClick: handleSave,
        children: saveText,
        color: ButtonColor.blue,
        disabled: channelScenariosStore.savingStage.isLoading,
        isLoading: channelScenariosStore.savingStage.isLoading
      }}
    >
      {getDescription({
        editedChannelName: channelsModalStore.editedChannelName,
        pendingChannelChangeIsSelection:
          channelsModalStore.pendingChannelChangeIsSelection
      })}
    </FormModal>
  );
};

export default observer(ChannelScenariosNavigationConfirmModal);
